import { Map, Set } from 'immutable';
import { selector } from '@/lib/selectors';
import type { JeFieldRearrangementState, FieldRearrangementConfigStorage } from '@/domain/jeFieldRearrangement/types';
import { buildConfigKey } from '@/domain/jeFieldRearrangement/types';

interface RootState {
  jeFieldRearrangement: JeFieldRearrangementState;
  journal: unknown; // Immutable Record — use .get() for access
  env: unknown; // Immutable Record — contains rtl flag
}

const EMPTY_CONFIG: FieldRearrangementConfigStorage = {
  positions: {},
  hidden: [],
  shown: [],
  locked: [],
};

export const jeFieldRearrangement = (state: RootState) => state.jeFieldRearrangement;

/** Read jeInvoiceType directly from journal state to avoid circular import.
 *  Journal state is an Immutable Record — use property accessor (works on Records). */
const jeInvoiceType = (state: RootState): string => {
  const j = state.journal as { jeInvoiceType?: string; get?: (key: string) => unknown };
  if (!j) return '';
  // Immutable Record: property accessor works directly
  if (j.jeInvoiceType !== undefined) return j.jeInvoiceType || '';
  // Fallback: .get()
  if (typeof j.get === 'function') return (j.get('jeInvoiceType') as string) || '';
  return '';
};

/** Read rtl directly from env state to avoid circular import. */
const rtlEnabled = (state: RootState): boolean => {
  const e = state.env as { get?: (key: string) => unknown };
  if (!e) return false;
  if (typeof e.get === 'function') return !!e.get('rtl');
  return false;
};

/** Current config for the active transaction type + direction (plain object) */
export const currentFieldRearrangementConfigSelector = selector(
  jeFieldRearrangement,
  jeInvoiceType,
  rtlEnabled,
  (state: JeFieldRearrangementState, transactionType: string, rtl: boolean): FieldRearrangementConfigStorage => {
    if (!transactionType) return EMPTY_CONFIG;
    const key = buildConfigKey(transactionType, rtl);
    return (state as any).configsByType.get(key) || EMPTY_CONFIG;
  },
);

/** Immutable Map<string, string> of positions for the current transaction type */
export const fieldRearrangementPositionsSelector = selector(
  currentFieldRearrangementConfigSelector,
  (config: FieldRearrangementConfigStorage) => Map<string, string>(Object.entries(config.positions || {})),
);

/** Immutable Set<string> of hidden field names */
export const fieldRearrangementHiddenSelector = selector(
  currentFieldRearrangementConfigSelector,
  (config: FieldRearrangementConfigStorage) => Set<string>(config.hidden || []),
);

/** Immutable Set<string> of shown field names */
export const fieldRearrangementShownSelector = selector(
  currentFieldRearrangementConfigSelector,
  (config: FieldRearrangementConfigStorage) => Set<string>(config.shown || []),
);

/** Immutable Set<string> of locked field names */
export const fieldRearrangementLockedSelector = selector(
  currentFieldRearrangementConfigSelector,
  (config: FieldRearrangementConfigStorage) => Set<string>(config.locked || []),
);
